// Termin/Aufgabe neu oder bearbeiten – die EINE Eingabe der Suite, als Seitenleiste RECHTS im Kalender (Skizze „Kalender-Ordnung“).
// Am PC steht sie neben dem Kalender, am Handy füllt sie den ganzen Bildschirm. Es öffnet sich kein überlagerndes Fenster.
// Alle „+ Termin“/„+ Aufgabe“-Wege der Suite landen über windowSync.terminPlanen() im Kalender und damit hier (A-027, Kalender-Ordnung Stufe 1).
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TitleInputWithVorlagen from "@/components/TitleInputWithVorlagen";
import { TextareaWithAI } from "@/components/TextareaWithAI";
import MonteurMehrfachAuswahl from "@/components/MonteurMehrfachAuswahl";
import AushilfeLinkDialog from "@/components/AushilfeLinkDialog";
import { X, User as UserIcon, Folder, Briefcase, Wrench, AlertTriangle, Link2, ListChecks, ChevronLeft } from "lucide-react";
import VorOrtZeile from "@/components/VorOrtZeile"; // A-020 P4
import AuswahlFeld from "@/components/AuswahlFeld";
import { typBezeichnung, TYP_GRUNDBESTAND, TYP_STANDARD } from "@/lib/terminTyp";
import NewProjektDialog from "@/components/NewProjektDialog";
import { useF1Help, hilfeOeffnen } from "@/lib/useF1Help";
import { terminGeaendert, terminPlanen } from "@/lib/windowSync";
import { terminBestaetigen } from "@/lib/terminGo";
import { TerminSendDialog } from "@/components/TerminSendDialog";
import UhrzeitFeld, { fuegeDatumZeit, teileDatumZeit } from "@/components/UhrzeitFeld";
import { findeKonflikte, konfliktText, monteureVon, terminZeiten } from "./kalenderLogik";

const WIEDERHOLUNG_LABELS = { einmalig: "Einmalig", taeglich: "Täglich", woechentlich: "Wöchentlich", monatlich: "Monatlich" };


const alsLokal = (s) => {
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s;
  try {
    const d = new Date(s); const z = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
  } catch { return String(s).slice(0, 16); }
};

// grün = schon aus dem Kunden/Projekt/Kalender ausgefüllt (Skizze)
const GRUEN = "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30";
const FELD = "w-full border rounded-sm p-2 text-sm";

/**
 * Props:
 *  termin / aufgabe  vorhandener Eintrag zum Bearbeiten (sonst neu), modus "termin" | "aufgabe" (Startwert, Umschalter nur beim Neuanlegen)
 *  vorbelegung       { kunde_id, projekt_id, einsatz_id, ort, titel, monteure, start, ende, faellig_am }
 *  zeitVorgabe       { start, ende, monteur, n } – Klick/Ziehen im Kalender, solange die Leiste offen ist (n zählt hoch)
 *  monteureExtern    Mitarbeiter aus der Kopfleiste des Kalenders (eine Quelle: Leiste und Kopfleiste zeigen dieselbe Auswahl)
 *  onMonteure(liste) / onModus(modus) / onEntwurf({start,ende,titel,kunde_id,projekt_id}) – Meldungen an den Kalender (Kopfleiste, Markierung im Raster)
 *  mobilVersteckt    Handy: bei einer neuen Planung zuerst nur Kalender + Kopfleiste, die Eingabe öffnet sich erst mit der Zeitwahl (Skizze Bild 3)
 *  onZurueck         Handy: Pfeil „← Kalender“ (zurück zum Kalender, Eingabe bleibt erhalten)
 */
export const TerminSeitenleiste = ({
  termin: terminRoh, aufgabe = null, modus: modusStart, kunden, projekte, aufgaben, mitarbeiter, selectedTarget, vorbelegung, termine = [],
  zeitVorgabe = null, monteureExtern = null, onMonteure = null, onModus = null, onEntwurf = null, mobilVersteckt = false, onZurueck = null,
  onProjektOeffnen = null, onClose, onSaved,
}) => {
  const termin = useMemo(() => (terminRoh ? { ...terminRoh, start: alsLokal(terminRoh.start), ende: alsLokal(terminRoh.ende) } : null), [terminRoh]);
  // Ein Formular für Termin UND Aufgabe (Umschalter oben; beim Bearbeiten steht der Modus fest)
  const [modus, setModus] = useState(aufgabe ? "aufgabe" : (termin ? "termin" : (modusStart || "termin")));
  const istAufgabe = modus === "aufgabe";
  useF1Help(istAufgabe ? "hilfe_aufgaben" : "hilfe_termine");   // F1 = Hilfe zu Termin bzw. Aufgabe
  const isEdit = istAufgabe ? !!aufgabe : !!termin;
  const umschalter = !termin && !aufgabe;
  const quelle = aufgabe || termin;
  const heuteISO = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const [ag, setAg] = useState({
    kategorie: aufgabe?.kategorie || "",
    prioritaet: aufgabe?.prioritaet || "normal",
    zugewiesen_an: aufgabe ? (aufgabe.zugewiesen_an || "") : (vorbelegung?.monteure?.[0] || ""),
    faellig_am: aufgabe ? (aufgabe.faellig_am || "") : (vorbelegung?.faellig_am || (vorbelegung?.start ? String(vorbelegung.start).slice(0, 10) : heuteISO)),
    wiederholung: aufgabe?.wiederholung || "einmalig",
    status: aufgabe?.status || "offen",
  });
  const updAg = (k, v) => setAg(d => ({ ...d, [k]: v }));
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let lebt = true;
    api.get("/module-aufgaben/meta").then(r => { if (lebt) setMeta(r.data || {}); }).catch(() => {});
    return () => { lebt = false; };
  }, []);
  const initialKunde = quelle
    ? (quelle?.kunde_id || "")
    : (vorbelegung?.kunde_id || (selectedTarget?.type === "kunde" ? selectedTarget.id : (selectedTarget?.type === "projekt" ? (selectedTarget.kunde_id || "") : "")));
  const initialProjekt = quelle
    ? (quelle?.projekt_id || "")
    : (vorbelegung?.projekt_id || (selectedTarget?.type === "projekt" ? selectedTarget.id : ""));
  const [data, setData] = useState({
    titel: quelle?.titel || vorbelegung?.titel || "",
    typ: typBezeichnung(termin?.typ) || TYP_STANDARD,   // alter Schlüssel -> Bezeichnung (Liste "termin_typ")
    start: termin?.start || vorbelegung?.start || "",
    ende: termin?.ende || vorbelegung?.ende || "",
    ort: termin?.ort || vorbelegung?.ort || "",
    beschreibung: quelle?.beschreibung || "",
    kunde_id: initialKunde,
    projekt_id: initialProjekt,
    aufgabe_id: termin?.aufgabe_id || "",
    einsatz_id: termin?.einsatz_id || vorbelegung?.einsatz_id || "",
    monteur_username: termin?.monteur_username || vorbelegung?.monteure?.[0] || vorbelegung?.monteur || "",
    monteur_usernames: termin ? monteureVon(termin) : (vorbelegung?.monteure?.length ? vorbelegung.monteure : (vorbelegung?.monteur ? [vorbelegung.monteur] : [])),
  });
  // Einsätze zur Auswahl (vom Kunden/Projekt gefiltert; der gewählte bleibt immer in der Liste)
  const [einsaetze, setEinsaetze] = useState([]);
  useEffect(() => {
    let lebt = true;
    api.get("/einsaetze").then(r => { if (lebt) setEinsaetze(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    return () => { lebt = false; };
  }, []);
  // Knopf „+ Neu“ am Einsatz: öffnet das kleine Fenster „Neuer Einsatz“ (Betreff Pflicht, Ort vorbelegt). „Einsatz anlegen“ nutzt den
  // vorhandenen Weg POST /einsaetze/from-kunde/{kunde_id}; Doppelklick legt nichts doppelt an (Sperre). Gibt es für Kunde + Projekt schon
  // Einsätze, steht oben der Hinweis „Es gibt schon: … – diesen nehmen?“.
  const [einsatzDialog, setEinsatzDialog] = useState(false);
  const [einsatzAnlegen, setEinsatzAnlegen] = useState(false);
  const [dlgBetreff, setDlgBetreff] = useState("");
  const [dlgOrt, setDlgOrt] = useState("");
  const anlegenSperre = useRef(false);
  const datumKurz = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("de-DE") : ""; } catch { return ""; } };
  const einsatzName = (e) => `${e.betreff || "Ohne Betreff"}${e.created_at ? ` · angelegt ${datumKurz(e.created_at)}` : ""}${e.status && e.status !== "aktiv" ? ` · ${e.status}` : ""}`;
  const projektTitelAktuell = () => { const pr = [...neueProjekte, ...projekte].find(x => x.id === data.projekt_id); return (pr?.titel || pr?.name || "").trim(); };
  const einsatzDialogOeffnen = () => {
    if (!data.kunde_id) { toast.error("Bitte zuerst Kunde wählen"); return; }
    setDlgBetreff((data.titel || "").trim() || projektTitelAktuell());
    setDlgOrt((data.ort || "").trim());
    setEinsatzDialog(true);
  };
  const vorhandeneEinsaetze = einsaetze.filter(e => e.kunde_id === data.kunde_id && (!data.projekt_id || e.projekt_id === data.projekt_id));
  const neuerEinsatz = async () => {
    if (anlegenSperre.current) return;
    if (!data.kunde_id) { toast.error("Bitte zuerst Kunde wählen"); return; }
    if (!dlgBetreff.trim()) { toast.error("Bitte einen Betreff eintragen"); return; }
    anlegenSperre.current = true;
    setEinsatzAnlegen(true);
    try {
      const body = { betreff: dlgBetreff.trim() };
      if (data.projekt_id) body.projekt_id = data.projekt_id;
      const ort = dlgOrt.trim();
      if (ort) {
        const m = ort.match(/^(.*?),\s*(\d{5})\s+(.+)$/);
        if (m) { body.objekt_strasse = m[1]; body.objekt_plz = m[2]; body.objekt_ort = m[3]; } else body.objekt_strasse = ort;
      }
      const res = await api.post(`/einsaetze/from-kunde/${data.kunde_id}`, body);
      const e = res.data;
      if (e?.id) { setEinsaetze(l => [e, ...l.filter(x => x.id !== e.id)]); upd("einsatz_id", e.id); setEinsatzDialog(false); toast.success("Einsatz angelegt"); }
    } catch { toast.error("Einsatz konnte nicht angelegt werden"); }
    finally { setEinsatzAnlegen(false); anlegenSperre.current = false; }
  };
  const einsaetzeFiltered = einsaetze.filter(e =>
    e.id === data.einsatz_id ||
    ((!data.kunde_id || e.kunde_id === data.kunde_id) && (!data.projekt_id || !e.projekt_id || e.projekt_id === data.projekt_id)));
  // A-020 P4: „Vor Ort“ (Ansprechpartner + Baustelle) aus dem gewählten Projekt
  const [vorOrt, setVorOrt] = useState(null);
  useEffect(() => {
    let lebt = true;
    if (!data.projekt_id) { setVorOrt(null); return undefined; }
    api.get(`/module-projekte/${data.projekt_id}/vor-ort`).then(r => { if (lebt) setVorOrt(r.data || null); }).catch(() => { if (lebt) setVorOrt(null); });
    return () => { lebt = false; };
  }, [data.projekt_id]);
  // Ort: leer -> Baustelle des Projekts, sonst Adresse des Kunden (nur bei neuem Termin, Handeingabe bleibt)
  const [ortHand, setOrtHand] = useState(false);
  useEffect(() => {
    if (termin || ortHand || vorbelegung?.ort) return;
    const k = kunden.find(x => x.id === data.kunde_id);
    const kAdr = k ? [k.address || [k.strasse, k.hausnummer].filter(Boolean).join(" "), [k.plz, k.ort || k.stadt].filter(Boolean).join(" ")].filter(Boolean).join(", ") : "";
    const neu = vorOrt?.baustelle || kAdr || "";
    setData(d => (d.ort === neu ? d : { ...d, ort: neu }));
  }, [data.kunde_id, vorOrt, kunden]); // eslint-disable-line
  const [saving, setSaving] = useState(false);
  const [wannHand, setWannHand] = useState(false);   // Zeit von Hand geändert -> nicht mehr grün
  const [aushilfeLink, setAushilfeLink] = useState(false);   // A-027 S4
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  // Mehrere Monteure: das alte Feld bleibt immer der erste Eintrag
  const setzeMonteure = (liste) => setData(d => ({ ...d, monteur_usernames: liste, monteur_username: liste[0] || "" }));
  const anzeigeName = (u) => mitarbeiter.find(m => m.username === u)?.anzeige_name || u;

  // --- Zusammenspiel mit dem Kalender -------------------------------------------------------------
  // Mitarbeiter: eine Auswahl für Seitenleiste und Kopfleiste (Aufgabe: nur einer)
  const aktuelleMonteure = istAufgabe ? (ag.zugewiesen_an ? [ag.zugewiesen_an] : []) : data.monteur_usernames;
  const monteureKey = JSON.stringify(aktuelleMonteure);
  useEffect(() => { onMonteure?.(aktuelleMonteure); }, [monteureKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!monteureExtern || JSON.stringify(monteureExtern) === monteureKey) return;
    if (istAufgabe) updAg("zugewiesen_an", monteureExtern[0] || "");
    else setzeMonteure(monteureExtern);
  }, [JSON.stringify(monteureExtern)]); // eslint-disable-line react-hooks/exhaustive-deps
  // Beim Umschalten Termin <-> Aufgabe den gewählten Mitarbeiter mitnehmen
  useEffect(() => {
    if (istAufgabe && !ag.zugewiesen_an && data.monteur_usernames[0] && !aufgabe) updAg("zugewiesen_an", data.monteur_usernames[0]);
    if (!istAufgabe && !data.monteur_usernames.length && ag.zugewiesen_an && !termin) setzeMonteure([ag.zugewiesen_an]);
    onModus?.(modus);
  }, [modus]); // eslint-disable-line react-hooks/exhaustive-deps
  // Klick oder Ziehen im Kalender, solange die Leiste offen ist: Zeit (Aufgabe: Tag) übernehmen
  useEffect(() => {
    if (!zeitVorgabe?.n) return;
    setWannHand(false);
    if (istAufgabe) updAg("faellig_am", String(zeitVorgabe.start).slice(0, 10));
    else setData(d => ({ ...d, start: zeitVorgabe.start, ende: zeitVorgabe.ende || d.ende, ...(zeitVorgabe.monteur && !d.monteur_usernames.length ? { monteur_usernames: [zeitVorgabe.monteur], monteur_username: zeitVorgabe.monteur } : {}) }));
  }, [zeitVorgabe?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  // Entwurf an den Kalender melden: gestrichelter Kasten „Neuer Termin“ im Raster, Bezug in der Kopfleiste
  useEffect(() => {
    if (!onEntwurf) return;
    const z = !istAufgabe && !termin ? terminZeiten({ start: data.start, ende: data.ende }) : null;
    onEntwurf({ start: z?.start || null, ende: z?.ende || null, titel: data.titel, kunde_id: data.kunde_id, projekt_id: data.projekt_id });
  }, [istAufgabe, data.start, data.ende, data.titel, data.kunde_id, data.projekt_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/Ende: Datum + Uhrzeit getrennt; wer den Start ändert, verschiebt das Ende mit gleicher Dauer (wie Google Kalender)
  const startT = teileDatumZeit(data.start);
  const endeT = teileDatumZeit(data.ende);
  const setzeStart = (datum, zeit) => {
    setWannHand(true);
    setData(d => {
      const neu = fuegeDatumZeit(datum, zeit);
      const alt = new Date(d.start), ende = new Date(d.ende);
      let neuEnde = d.ende;
      if (d.start && d.ende && !Number.isNaN(alt.getTime()) && !Number.isNaN(ende.getTime())) {
        const e = new Date(new Date(neu).getTime() + (ende.getTime() - alt.getTime()));
        const z = (n) => String(n).padStart(2, "0");
        neuEnde = `${e.getFullYear()}-${z(e.getMonth() + 1)}-${z(e.getDate())}T${z(e.getHours())}:${z(e.getMinutes())}`;
      }
      return { ...d, start: neu, ende: neuEnde };
    });
  };
  const setzeEnde = (datum, zeit) => { setWannHand(true); setData(d => ({ ...d, ende: zeit || datum ? fuegeDatumZeit(datum, zeit) : "" })); };
  // Warnung bei Doppelbelegung (nur Hinweis, speichern geht immer)
  const konflikte = useMemo(() => {
    const z = terminZeiten({ start: data.start, ende: data.ende });
    if (!z || !data.monteur_usernames.length || termin?.status === "abgesagt") return [];
    return findeKonflikte({ id: termin?.id || "", start: z.start, ende: z.ende, monteure: data.monteur_usernames }, termine);
  }, [data.start, data.ende, data.monteur_usernames, termine, termin]);

  // Filter Projekte nach gewähltem Kunden
  // Neu angelegte Projekte (Knopf „+ Neu“) stehen sofort in der Liste
  const [neueProjekte, setNeueProjekte] = useState([]);
  const [neuesProjekt, setNeuesProjekt] = useState(false);
  const alleProjekte = [...neueProjekte, ...projekte.filter(p => !neueProjekte.some(n => n.id === p.id))];
  const projekteFiltered = data.kunde_id
    ? alleProjekte.filter(p => p.kunde_id === data.kunde_id)
    : alleProjekte;
  // Regel des Programms: Termin mit Kunde braucht ein Projekt (oder Einsatz)
  const projektFehlt = !istAufgabe && !!data.kunde_id && !data.projekt_id && !data.einsatz_id;
  // grün = aus Kunde/Projekt/Kalender vorbelegt (nur beim Neuanlegen, solange nicht von Hand geändert)
  const gruenKunde = !isEdit && !!initialKunde && data.kunde_id === initialKunde;
  const gruenProjekt = !isEdit && !!initialProjekt && data.projekt_id === initialProjekt;
  const gruenOrt = !isEdit && !istAufgabe && !!data.ort && !ortHand;
  const gruenWann = !isEdit && !wannHand && (istAufgabe ? (!!vorbelegung?.faellig_am || !!vorbelegung?.start || !!zeitVorgabe?.n) : !!data.start);
  const gibtGruen = gruenKunde || gruenProjekt || gruenOrt || gruenWann;


  const save = async () => {
    if (!data.titel.trim()) { toast.error("Titel erforderlich"); return; }
    if (istAufgabe) {
      setSaving(true);
      try {
        const nutz = { titel: data.titel, beschreibung: data.beschreibung, kunde_id: data.kunde_id, projekt_id: data.projekt_id, ...ag };
        if (isEdit) { await api.put(`/module-aufgaben/${aufgabe.id}`, nutz); toast.success("Aufgabe aktualisiert"); }
        else { const { status: _s, ...neu } = nutz; await api.post("/module-aufgaben", neu); toast.success("Aufgabe angelegt"); }
        terminGeaendert();
        onSaved();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
      } finally { setSaving(false); }
      return;
    }
    if (!data.start.trim()) { toast.error("Startzeit erforderlich"); return; }
    const bezugGeaendert = !isEdit || data.kunde_id !== (termin.kunde_id || "");
    if (projektFehlt && bezugGeaendert) { toast.error("Bitte ein Projekt wählen (oder mit „+ Neu“ anlegen)"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/module-termine/${termin.id}`, data);
      } else {
        await api.post("/module-termine", data);
      }
      // EINE Meldung (zwei Meldungen übereinander waren unlesbar): Erfolg und, falls nötig, die Doppelbelegung in einem Satz
      const erfolg = isEdit ? "Termin aktualisiert" : "Termin angelegt – wartet auf GO";
      if (konflikte.length) toast.warning(`${erfolg} – Achtung: ${konfliktText(konflikte, anzeigeName)}`, { duration: 9000 });
      else toast.success(erfolg);
      terminGeaendert();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  // GO und „An Kalender senden“ (wie in der Terminliste): derselbe Weg, dieselben Bausteine (lib/terminGo.js, TerminSendDialog)
  const [statusLokal, setStatusLokal] = useState(null);
  const [sendenOffen, setSendenOffen] = useState(false);
  const terminStatus = statusLokal || termin?.status;
  const goBestaetigen = async () => {
    const ok = await terminBestaetigen(termin, data.start ? new Date(data.start).toLocaleDateString("de-DE") : "");
    if (ok) { setStatusLokal("bestaetigt"); setSendenOffen(true); }
  };
  const markErledigt = async () => {
    if (!data.beschreibung.trim()) {
      toast.error("Bitte Beschreibung ausfuellen (was wurde gemacht / was fehlt noch), bevor der Termin als erledigt markiert werden kann");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/module-termine/${termin.id}`, data);
      await api.patch(`/module-termine/${termin.id}/erledigt`, { beschreibung: data.beschreibung });
      toast.success("Termin als erledigt markiert");
      terminGeaendert();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht als erledigt markiert werden");
    } finally {
      setSaving(false);
    }
  };

  const titelText = istAufgabe ? (isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe") : (isEdit ? "Termin bearbeiten" : "Neuer Termin");
  return (
    <aside
      className={`bg-background ${mobilVersteckt ? "hidden md:flex" : "flex"} flex-col fixed inset-0 z-50 overflow-y-auto md:static md:z-auto md:inset-auto md:w-[22rem] md:shrink-0 md:sticky md:top-2 md:self-start md:max-h-[calc(100vh-1rem)] md:border md:rounded-md ${istAufgabe ? "md:border-t-4 md:border-t-violet-500" : ""}`}
      data-testid="termin-seitenleiste" data-modus={modus} data-bearbeiten={isEdit ? "ja" : "nein"}
    >
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b sticky top-0 z-10 bg-primary text-primary-foreground md:bg-background md:text-foreground ${istAufgabe ? "md:bg-violet-50 dark:md:bg-violet-950/30" : ""}`}>
        <button type="button" onClick={onZurueck || onClose} className="md:hidden flex items-center gap-1 text-sm font-medium" data-testid="btn-seitenleiste-zurueck" aria-label="Zurück zum Kalender">
          <ChevronLeft className="w-4 h-4" /> Kalender
        </button>
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 min-w-0 truncate" data-testid="fenster-titel">
          {istAufgabe && <ListChecks className="w-5 h-5 text-violet-600 shrink-0 hidden md:block" />}
          <span className="truncate">{titelText}</span>
        </h2>
        <div className="flex items-center gap-1 ml-auto">
          <button type="button" onClick={() => hilfeOeffnen(istAufgabe ? "hilfe_aufgaben" : "hilfe_termine")} className="w-8 h-8 rounded-full border border-current/40 text-sm font-semibold hover:bg-muted/30" title="Hilfe (F1)" aria-label="Hilfe" data-testid="btn-fenster-hilfe">?</button>
          <button type="button" onClick={onClose} className="hidden md:block p-1 hover:bg-muted rounded-sm" aria-label="Schließen" data-testid="btn-seitenleiste-schliessen">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      {umschalter && (
        <div className="px-3 pt-3 flex gap-1" role="tablist" data-testid="umschalter-termin-aufgabe">
          <button type="button" role="tab" aria-selected={!istAufgabe} onClick={() => setModus("termin")} className={`flex-1 px-3 py-1.5 text-sm rounded-sm border ${!istAufgabe ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`} data-testid="umschalter-termin">Termin</button>
          <button type="button" role="tab" aria-selected={istAufgabe} onClick={() => setModus("aufgabe")} className={`flex-1 px-3 py-1.5 text-sm rounded-sm border ${istAufgabe ? "bg-violet-600 text-white border-violet-600" : "hover:bg-muted"}`} data-testid="umschalter-aufgabe">Aufgabe</button>
        </div>
      )}

      <div className="p-3 space-y-3 flex-1" data-testid={istAufgabe ? "fenster-aufgabe" : "fenster-termin"}>
        <TitleInputWithVorlagen
          value={data.titel}
          onChange={(v) => upd("titel", v)}
          docType={istAufgabe ? "aufgabe_titel" : "termin_titel"}
          fallbackDocTypes={istAufgabe ? ["aufgabe"] : ["termin"]}
          label="Titel"
          required
          autoFocus
          placeholder={istAufgabe ? "z.B. Türrahmen vor Ort vermessen" : "z.B. Besichtigung Familie Müller"}
        />

        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> Kunde</label>
          <select value={data.kunde_id} onChange={(e) => upd("kunde_id", e.target.value)} className={`${FELD} ${gruenKunde ? GRUEN : ""}`} data-testid="select-kunde">
            <option value="">— kein Kunde —</option>
            {kunden.map(k => {
              const name = `${k.vorname || ""} ${k.nachname || ""}`.trim() || k.name || "Unbenannt";
              return <option key={k.id} value={k.id}>{name}{k.firma ? ` (${k.firma})` : ""}</option>;
            })}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Folder className="w-3.5 h-3.5" /> Projekt</label>
          <div className="flex items-center gap-1">
            <select value={data.projekt_id} onChange={(e) => upd("projekt_id", e.target.value)} className={`${FELD} ${gruenProjekt ? GRUEN : ""}`} data-testid="select-projekt" disabled={!projekteFiltered.length}>
              <option value="">{data.kunde_id ? "— bitte wählen —" : "— kein Projekt —"}</option>
              {projekteFiltered.map(p => <option key={p.id} value={p.id}>{p.titel || p.name || p.id}</option>)}
            </select>
            {data.kunde_id && (
              <button type="button" onClick={() => setNeuesProjekt(true)} className="text-xs px-2 py-2 border rounded-sm hover:bg-muted whitespace-nowrap" data-testid="termin-btn-neues-projekt">+ Neu</button>
            )}
          </div>
          {onProjektOeffnen && data.kunde_id && data.projekt_id && (
            <button type="button" onClick={() => onProjektOeffnen(data.kunde_id, data.projekt_id)} className="mt-1 text-xs px-2 py-1.5 border rounded-sm hover:bg-muted whitespace-nowrap" data-testid="termin-btn-projekt-oeffnen" title="Werkbank dieses Projekts im Hauptfenster öffnen">Projekt öffnen</button>
          )}
          {projektFehlt && <div className="text-xs text-amber-700 mt-1" data-testid="termin-projekt-hinweis">Zu einem Kunden gehört ein Projekt – bitte wählen oder „+ Neu“.</div>}
          {vorOrt?.baustelle && <div className="text-xs text-muted-foreground mt-1" data-testid="termin-baustelle">Baustelle: {vorOrt.baustelle}</div>}
          <VorOrtZeile kontakt={vorOrt?.kontakt} testId="termin-vor-ort" />
        </div>

        {!istAufgabe && <>
          <div>
            <label className="block text-sm font-medium mb-1">Ort / Adresse</label>
            <input value={data.ort} onChange={(e) => { setOrtHand(true); upd("ort", e.target.value); }} className={`${FELD} ${gruenOrt ? GRUEN : ""}`} placeholder="Optional" data-testid="input-ort" />
          </div>
          <div className="space-y-2" data-testid="zeitfelder">
            <div>
              <label className="block text-sm font-medium mb-1">Wann · Start *</label>
              <div className="flex gap-2">
                <input type="date" value={startT.datum} onChange={(e) => setzeStart(e.target.value, startT.zeit || "08:00")} className={`flex-1 min-w-0 border rounded-sm p-2 text-sm ${gruenWann ? GRUEN : ""}`} data-testid="input-start-datum" />
                <div className="w-28 shrink-0"><UhrzeitFeld value={startT.zeit} onChange={(z) => setzeStart(startT.datum || heuteISO, z)} testId="input-start" ariaLabel="Startzeit" /></div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ende</label>
              <div className="flex gap-2">
                <input type="date" value={endeT.datum} onChange={(e) => setzeEnde(e.target.value, endeT.zeit || startT.zeit || "09:00")} className={`flex-1 min-w-0 border rounded-sm p-2 text-sm ${gruenWann ? GRUEN : ""}`} data-testid="input-ende-datum" />
                <div className="w-28 shrink-0"><UhrzeitFeld value={endeT.zeit} ab={endeT.datum && endeT.datum === startT.datum ? startT.zeit : null} onChange={(z) => setzeEnde(endeT.datum || startT.datum || heuteISO, z)} testId="input-ende" ariaLabel="Endzeit" /></div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Oder im Kalender in die freie Zeit klicken bzw. darüber ziehen.</p>
          </div>
          <MonteurMehrfachAuswahl
            mitarbeiter={mitarbeiter}
            value={data.monteur_usernames}
            onChange={setzeMonteure}
            label="Mitarbeiter (mehrere möglich)"
            testId="select-monteure"
            hinweis={konflikte.length > 0 && (
              <div className="mt-1.5 flex items-start gap-2 rounded-sm border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-2 py-1.5 text-xs text-red-800 dark:text-red-200" data-testid="termin-doppelbelegung">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><b>Doppelbelegung:</b> {konfliktText(konflikte, anzeigeName)} Sie können trotzdem speichern.</span>
              </div>
            )}
          />
          <AuswahlFeld docType="termin_typ" label="Typ" value={data.typ} ersatz={TYP_GRUNDBESTAND}
            onChange={(v) => upd("typ", v)} stichwoerter={false} testId="select-typ" />
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> Interne Aufgabe</label>
            <select value={data.aufgabe_id} onChange={(e) => upd("aufgabe_id", e.target.value)} className={FELD} data-testid="select-aufgabe">
              <option value="">— keine Aufgabe —</option>
              {aufgaben.map(a => <option key={a.id} value={a.id}>{a.titel}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Einsatz</label>
            <div className="flex items-center gap-1">
              <select value={data.einsatz_id} onChange={(e) => upd("einsatz_id", e.target.value)} className={FELD} data-testid="select-einsatz">
                <option value="">— kein Einsatz —</option>
                {einsaetzeFiltered.map(e => <option key={e.id} value={e.id}>{einsatzName(e)}{e.kunde_name && !data.kunde_id ? ` (${e.kunde_name})` : ""}</option>)}
              </select>
              <button type="button" onClick={einsatzDialogOeffnen} disabled={!data.kunde_id} title={data.kunde_id ? "Neuen Einsatz zu diesem Kunden anlegen" : "Bitte zuerst Kunde wählen"}
                className="text-xs px-2 py-2 border rounded-sm hover:bg-muted whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed" data-testid="termin-btn-neuer-einsatz">+ Neu</button>
            </div>
            {!data.kunde_id && <div className="text-xs text-muted-foreground mt-1" data-testid="termin-einsatz-hinweis">Bitte zuerst Kunde wählen.</div>}
          </div>
        </>}
        {istAufgabe && (
          <div className="space-y-3" data-testid="aufgabe-felder">
            <div>
              <label className="block text-sm font-medium mb-1">Fällig am</label>
              <input type="date" value={ag.faellig_am ? ag.faellig_am.slice(0, 10) : ""} onChange={(e) => { setWannHand(true); updAg("faellig_am", e.target.value); }} className={`${FELD} ${gruenWann ? GRUEN : ""}`} data-testid="input-faellig" />
              <p className="text-xs text-muted-foreground mt-1">Oder im Kalender den Tag antippen.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zugewiesen an</label>
              <select value={ag.zugewiesen_an} onChange={(e) => updAg("zugewiesen_an", e.target.value)} className={FELD} data-testid="select-mitarbeiter">
                <option value="">— Niemand —</option>
                {mitarbeiter.map(m => <option key={m.username} value={m.username}>{m.anzeige_name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Priorität</label>
                <select value={ag.prioritaet} onChange={(e) => updAg("prioritaet", e.target.value)} className={FELD} data-testid="select-prioritaet">
                  {(meta?.prioritaeten?.length ? meta.prioritaeten : ["niedrig", "normal", "hoch"]).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Wiederholung</label>
                <select value={ag.wiederholung} onChange={(e) => updAg("wiederholung", e.target.value)} className={FELD} data-testid="select-wiederholung">
                  {(meta?.wiederholungen?.length ? meta.wiederholungen : ["einmalig", "taeglich", "woechentlich", "monatlich"]).map(w => <option key={w} value={w}>{WIEDERHOLUNG_LABELS[w] || w}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Kategorie <span className="text-xs text-muted-foreground font-normal">· Pflege: Einstellungen → Textvorlagen</span></label>
              {(meta?.kategorien || []).length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">Noch keine Kategorien vorhanden (Einstellungen → Textvorlagen → „Aufgaben-Kategorie“).</div>
              ) : (
                <select value={ag.kategorie} onChange={(e) => updAg("kategorie", e.target.value)} className={FELD} data-testid="select-kategorie">
                  <option value="">— bitte wählen —</option>
                  {ag.kategorie && !meta.kategorien.includes(ag.kategorie) && <option value={ag.kategorie}>{ag.kategorie}</option>}
                  {meta.kategorien.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
            </div>
            {isEdit && (
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select value={ag.status} onChange={(e) => updAg("status", e.target.value)} className={FELD} data-testid="select-edit-status">
                  <option value="offen">Offen</option>
                  <option value="in_arbeit">In Arbeit</option>
                  <option value="erledigt">Erledigt</option>
                </select>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">{istAufgabe ? "Notiz / Beschreibung" : "Notiz / Arbeitsanweisung"}</label>
          <TextareaWithAI
            value={data.beschreibung || ""}
            onChange={(e) => upd("beschreibung", e.target.value)}
            rows={3}
            placeholder="Optional"
            feldLabel={istAufgabe ? "Aufgaben-Beschreibung" : "Termin-Beschreibung"}
            kontext={istAufgabe ? "aufgabe" : "termin"}
            testId={istAufgabe ? "aufgabe-beschreibung" : "termin-beschreibung"}
          />
        </div>
        {!isEdit && !istAufgabe && <p className="text-xs text-muted-foreground" data-testid="termin-go-hinweis">Der Termin wartet nach dem Anlegen auf Ihr GO (Status „Wartet auf GO“) und wird erst dann bestätigt.</p>}
        {gibtGruen && <p className="text-xs text-emerald-700 dark:text-emerald-400" data-testid="gruen-hinweis">Grün = schon ausgefüllt (aus Kunde, Projekt oder Kalender).</p>}
      </div>

      <div className="p-3 border-t flex flex-wrap justify-end gap-2 sticky bottom-0 bg-background">
        {isEdit && !istAufgabe && (
          <button onClick={() => setAushilfeLink(true)} className="px-3 py-2 text-sm border rounded-sm hover:bg-muted flex items-center gap-1 mr-auto" data-testid="btn-termin-aushilfe-link" title="Link ohne Anmeldung für eine Aushilfe: zeigt genau diesen Termin">
            <Link2 className="w-4 h-4" /> Link für Aushilfe
          </button>
        )}
        <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="btn-seitenleiste-abbrechen">Abbrechen</button>
        {isEdit && !istAufgabe && terminStatus === "wartet_auf_go" && (
          <button onClick={goBestaetigen} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 font-medium" data-testid="termin-btn-go"
            title="Termin bestätigen; danach wählen Sie, wer die Kalender-Mail (ICS) bekommt">
            GO – bestätigen
          </button>
        )}
        {isEdit && !istAufgabe && (terminStatus === "bestaetigt" || terminStatus === "im_kalender") && (
          <button onClick={() => setSendenOffen(true)} className="px-4 py-2 text-sm border border-blue-300 text-blue-700 rounded-sm hover:bg-blue-50" data-testid="termin-btn-an-kalender"
            title={terminStatus === "im_kalender" ? "Termin ist schon im Kalender; nach einer Änderung oder für den Mitarbeiter-Link erneut senden" : "Kalender-Mail (ICS) an Empfänger senden; danach steht der Termin „Im Kalender“"}>
            {terminStatus === "im_kalender" ? "Erneut an Kalender senden" : "An Kalender senden"}
          </button>
        )}
        {isEdit && !istAufgabe && (terminStatus === "bestaetigt" || terminStatus === "im_kalender") && (
          <button
            onClick={markErledigt}
            disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50"
            data-testid="termin-dialog-erledigt"
            title="Termin als erledigt markieren (Beschreibung erforderlich)"
          >
            ✓ Erledigt
          </button>
        )}
        <button
          onClick={save}
          disabled={saving}
          className={`px-4 py-2 text-sm text-primary-foreground rounded-sm disabled:opacity-50 ${istAufgabe ? "bg-violet-600 hover:bg-violet-700 text-white" : "bg-primary hover:bg-primary/90"}`}
          data-testid="btn-termin-save"
        >
          {saving ? "Speichere…" : isEdit ? "Speichern" : (istAufgabe ? "Aufgabe anlegen" : "Speichern (wartet auf GO)")}
        </button>
      </div>
      {/* Hilfsfenster (neues Projekt, Link für Aushilfe) über allem: per Portal, sonst liegt der Kalender darüber */}
      {neuesProjekt && createPortal(
        <div className="relative z-[60]"><NewProjektDialog
          kundeId={data.kunde_id}
          onClose={() => setNeuesProjekt(false)}
          onCreated={(p) => {
            setNeuesProjekt(false);
            if (p?.id) { setNeueProjekte(l => [{ kunde_id: data.kunde_id, ...p }, ...l.filter(x => x.id !== p.id)]); upd("projekt_id", p.id); }
          }}
        /></div>,
        document.body
      )}
      {einsatzDialog && createPortal(
        <div className="relative z-[60]">
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" data-testid="einsatz-dialog">
            <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold flex items-center gap-2"><Wrench className="w-5 h-5 text-primary" /> Neuer Einsatz</h3>
              {vorhandeneEinsaetze.length > 0 && (
                <div className="rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-sm space-y-1.5" data-testid="einsatz-vorhanden">
                  {vorhandeneEinsaetze.slice(0, 3).map(e => (
                    <div key={e.id} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 break-words">Es gibt schon: <b>{e.betreff || "Ohne Betreff"}</b> ({datumKurz(e.created_at)}) – diesen nehmen?</span>
                      <button type="button" onClick={() => { upd("einsatz_id", e.id); setEinsatzDialog(false); toast.success("Einsatz ausgewählt"); }}
                        className="shrink-0 text-xs px-2 py-1.5 border border-amber-400 rounded-sm bg-background hover:bg-muted" data-testid={`einsatz-nehmen-${e.id}`}>Diesen nehmen</button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Betreff *</label>
                <input value={dlgBetreff} onChange={(e) => setDlgBetreff(e.target.value)} className={FELD} autoFocus placeholder="z. B. Fenster tauschen" data-testid="einsatz-dialog-betreff" />
                {!dlgBetreff.trim() && <p className="text-xs text-amber-700 mt-1" data-testid="einsatz-dialog-pflicht">Bitte einen Betreff eintragen.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ort / Baustelle</label>
                <input value={dlgOrt} onChange={(e) => setDlgOrt(e.target.value)} className={FELD} data-testid="einsatz-dialog-ort" />
              </div>
              <div className="text-sm text-muted-foreground" data-testid="einsatz-dialog-projekt">Projekt: {projektTitelAktuell() || "kein Projekt gewählt"}</div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-1">
                <button type="button" onClick={() => setEinsatzDialog(false)} disabled={einsatzAnlegen} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted disabled:opacity-40" data-testid="einsatz-dialog-abbrechen">Abbrechen</button>
                <button type="button" onClick={neuerEinsatz} disabled={einsatzAnlegen || !dlgBetreff.trim()} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="einsatz-dialog-anlegen">{einsatzAnlegen ? "Lege an…" : "Einsatz anlegen"}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {sendenOffen && termin && createPortal(
        <div className="relative z-[60]"><TerminSendDialog termin_id={termin.id} onClose={() => setSendenOffen(false)} onSent={() => { setSendenOffen(false); setStatusLokal("im_kalender"); terminGeaendert(); onSaved(); }} /></div>,
        document.body
      )}
      {aushilfeLink && createPortal(<div className="relative z-[60]"><AushilfeLinkDialog termin={termin} onClose={() => setAushilfeLink(false)} /></div>, document.body)}
    </aside>
  );
};

/**
 * Einplanen aus dem Einsatz: der Kalender öffnet die Planung, vorbelegt mit allem, was der Einsatz schon weiß
 * (Kunde, Projekt, Einsatz, Ort = Objektadresse, Monteure über die Verknüpfung Mitarbeiter ↔ Anmeldung, Titel = Betreff).
 * Die Vorbelegung kommt zentral aus dem Backend (GET /einsaetze/{id}/einplanen-vorlage); der Weg in den Kalender ist
 * wie überall windowSync.terminPlanen(). Hier öffnet sich kein eigenes Formular.
 */
export const TerminEinplanenDialog = ({ einsatzId, onClose }) => {
  useEffect(() => {
    let lebt = true;
    (async () => {
      try {
        const v = await api.get(`/einsaetze/${einsatzId}/einplanen-vorlage`);
        const d = v.data || {};
        if (!lebt) return;
        await terminPlanen({
          modus: "termin", kunde_id: d.kunde_id, projekt_id: d.projekt_id, einsatz_id: d.einsatz_id || einsatzId,
          ort: d.ort, titel: d.titel, monteure: d.monteure || (d.monteur ? [d.monteur] : []),
        });
      } catch {
        toast.error("Einplanen konnte nicht vorbereitet werden");
      }
      if (lebt) onClose?.();
    })();
    return () => { lebt = false; };
  }, [einsatzId]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};
